import type { ThreadMessageLike } from "@assistant-ui/react";
import type {
  ChatMessage,
  CompanySource,
  EmailSource,
  MessageMetadata,
  StepRecord,
} from "../store/slices/chatSlice";

type MessagePart = NonNullable<ThreadMessageLike["content"]>[number];

export type ChatThreadMessage = ThreadMessageLike & {
  __original: ChatMessage;
};

// Map a single ChatMessage from Redux into the assistant-ui wire format.
// chatSlice is authoritative for the wire shape — both legacy (flat
// article_sources/email_sources/...) and the new nested metadata.sources
// shape are handled, mirroring how EnhancedSources reads them.
//
// `isStreaming` is true for the single message currently being streamed.
// It suppresses the rating part until the response finishes — matches
// the legacy Chatbot.tsx behavior of hiding rating for the last bot
// message while `isSending` is true.
export function chatMessageToThreadMessage(
  msg: ChatMessage,
  opts: { isStreaming?: boolean } = {},
): ChatThreadMessage {
  const role: "user" | "assistant" = msg.sender === "user" ? "user" : "assistant";

  const parts: MessagePart[] = [];

  // Thinking timeline renders ABOVE the answer. We emit each step as a
  // NATIVE assistant-ui part (`reasoning` for kind=='thinking',
  // `tool-call` for everything else) so the library's built-in
  // Reasoning + ToolGroup primitives render them with rich UI
  // (collapsible groups, args/result panels) — much better than the
  // legacy one-line MessageSteps. See thread.tsx GroupedParts
  // handler at the `tool-call` / `reasoning` cases.
  if (role === "assistant" && msg.metadata?.steps?.length) {
    for (const step of msg.metadata.steps) {
      parts.push(stepToPart(step));
    }
  }

  parts.push({
    type: "text",
    text: stripMarkdownSourcesSection(
      stripCitationTokens(msg.content ?? ""),
    ),
  });

  if (role === "assistant" && msg.metadata) {
    appendSourceParts(parts, msg.metadata);
  }

  // Rating button is rendered OUTSIDE the parts pipeline (see
  // <AssistantMessageRating /> in thread.tsx). The library's
  // external-store runtime doesn't reliably propagate late-added
  // data parts into s.message.parts when the parts array grows
  // post-streaming, so emitting an aui-rating data part here would
  // be silently dropped on render.

  // Status only on assistant messages — library rejects status on
  // user messages with a runtime invariant. We tell the runtime when
  // an assistant message is mid-stream so it cascades
  // `status.type = "running"` down to individual parts (ToolGroup
  // spinner + shimmer; our AGRANA red accent in tool-fallback.tsx).
  // Without this, the runtime's last-message heuristic never marks
  // tool-calls as running with useExternalStoreRuntime.
  const base: ThreadMessageLike = {
    id: msg.id,
    role,
    content: parts as ThreadMessageLike["content"],
  };
  if (role !== "assistant") return { ...base, __original: msg };
  return {
    ...base,
    status: opts.isStreaming
      ? ({ type: "running" } as const)
      : ({ type: "complete", reason: "stop" } as const),
    __original: msg,
  };
}

function appendSourceParts(out: MessagePart[], meta: MessageMetadata): void {
  const seenUrls = new Set<string>();
  const pushUrl = (url: string, title?: string) => {
    if (!url || seenUrls.has(url)) return;
    seenUrls.add(url);
    out.push({ type: "source", sourceType: "url", id: url, url, title });
  };

  // Articles — prefer new nested shape, fall back to legacy flat field.
  const articles = pickArrayShape(meta.sources?.articles) ?? meta.article_sources ?? [];
  for (const a of articles) {
    pushUrl(a.url ?? "", a.title);
  }

  // Web searches — backend emits one entry per query with a `urls` array.
  // Flatten so each cited URL gets its own SourceMessagePart slot.
  const webSearches = meta.sources?.web_searches ?? meta.web_searches ?? [];
  for (const w of webSearches) {
    for (const url of w.urls ?? []) {
      pushUrl(url ?? "");
    }
  }

  // Combined — the deduped union the backend builds from every source
  // kind it wants surfaced. For many real queries (esp. web-cited
  // answers) `articles` / `web_searches` come back empty but `combined`
  // is fully populated; without reading it the bubble would lose all
  // its source chips even though URLs exist in the metadata.
  // Email/company-shaped combined entries are split off into the
  // dedicated data parts below.
  const combined = meta.sources?.combined ?? [];
  const combinedEmails: EmailSource[] = [];
  const combinedCompanies: CompanySource[] = [];
  for (const c of combined) {
    if (c.type === "email") {
      combinedEmails.push(c as unknown as EmailSource);
      continue;
    }
    if (c.type === "company") {
      combinedCompanies.push(c as unknown as CompanySource);
      continue;
    }
    if (typeof c.url === "string") {
      pushUrl(c.url, typeof c.title === "string" ? c.title : undefined);
    }
  }

  // Emails and companies don't fit assistant-ui's native URL citations —
  // they're carried as ONE DataMessagePart per kind so the renderer can
  // group them into an accordion (mirror of EnhancedSources structure).
  const emails = pickArrayShape(meta.sources?.emails) ?? meta.email_sources ?? [];
  const emailsFinal = emails.length > 0 ? emails : combinedEmails;
  if (emailsFinal.length > 0) {
    out.push({ type: "data", name: "aui-emails", data: emailsFinal });
  }

  const companies = pickArrayShape(meta.sources?.companies) ?? meta.company_sources ?? [];
  const companiesFinal = companies.length > 0 ? companies : combinedCompanies;
  if (companiesFinal.length > 0) {
    out.push({ type: "data", name: "aui-companies", data: companiesFinal });
  }
}

// The nested shape allows arrays OR plain counts (e.g. `articles: 12`).
// Counts are summary info, not renderable sources — drop them.
function pickArrayShape<T>(value: T[] | number | undefined): T[] | undefined {
  return Array.isArray(value) ? value : undefined;
}

// Convert a single backend StepRecord into the matching native
// assistant-ui message part. `thinking` kind → ReasoningMessagePart
// (renders via the library's Reasoning primitive — chain-of-thought UI
// with collapsible content + streaming spinner). Everything else
// (tool_call, retrieval, web_search, …) → ToolCallMessagePart, which
// thread.tsx groups via GroupedParts into a single "Used N tools"
// accordion and renders each via the ToolFallback component (label,
// args panel, result panel, error panel).
//
// We don't have structured tool args from the backend — only `detail`
// (a string). We pass it through as both `argsText` (raw display) and
// `args.input` (a minimal JSON shape the type requires).
function stepToPart(step: StepRecord): MessagePart {
  if (step.kind === "thinking") {
    // Backend carries the chain-of-thought text in `result_preview`
    // (populated on the `step.end` event). `detail` is the live-stream
    // delta channel and is null for thinking. `label` is the literal
    // "Thinking" placeholder — only used as a last-resort fallback
    // for in-flight steps that haven't ended yet.
    return {
      type: "reasoning",
      text: step.result_preview ?? step.detail ?? step.label,
    };
  }

  return {
    type: "tool-call",
    toolCallId: step.id,
    toolName: humanizeToolName(step.label),
    args: { input: step.detail ?? "" },
    argsText: step.detail ?? "",
    result: step.result_preview ?? undefined,
    isError: step.status === "error",
  };
}

// `search_agrana_emails` → "Search agrana emails". Keeps the tool
// header readable in the assistant-ui ToolFallback "Used tool: X" UI.
function humanizeToolName(label?: string | null): string {
  if (!label) return "Step";
  const cleaned = label.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

// Strip the "## Sources" + "## Suggested follow-ups" markdown sections
// that the LLM appends at the end of most answers. The backend ALSO
// sends both as STRUCTURED data — `metadata.sources.*` becomes native
// assistant-ui `source` chips, `metadata.follow_up_questions` becomes
// the `ThreadSuggestion[]` slot that renders as clickable chips below
// the bubble. Without this stripper the user sees both copies (text
// duplicates the chips), which looks broken.
export function stripMarkdownSourcesSection(text: string): string {
  // Match a top-level Sources heading (## / ### / **Sources** bold-as-
  // heading variant) plus everything until the NEXT heading or end of
  // string. Anchored to "Sources" or "Source list" so we don't accidentally
  // eat content with the word "sources" elsewhere.
  return text
    .replace(
      /(\n|^)#{2,4}\s+Sources?(?:\s*list)?\s*\n[\s\S]*?(?=\n#{1,6}\s|$)/gi,
      "$1",
    )
    .replace(
      /(\n|^)\*\*Sources?\*\*\s*\n[\s\S]*?(?=\n#{1,6}\s|\n\*\*[A-Z]|$)/gi,
      "$1",
    )
    // Suggested follow-ups — markdown heading variant + bold-header
    // variant. Tolerates "follow-up" / "follow up" / "followup", and a
    // trailing colon or 's'. Eats the bullet list that follows until
    // the next heading or end of message.
    .replace(
      /(\n|^)#{2,4}\s+(?:Suggested\s+)?Follow[\s-]?ups?:?\s*\n[\s\S]*?(?=\n#{1,6}\s|$)/gi,
      "$1",
    )
    .replace(
      /(\n|^)\*\*(?:Suggested\s+)?Follow[\s-]?ups?:?\*\*\s*\n[\s\S]*?(?=\n#{1,6}\s|\n\*\*[A-Z]|$)/gi,
      "$1",
    )
    .replace(/\n{3,}/g, "\n\n")
    .trimEnd();
}

// Remove inline citation tokens emitted by anis_chatbot_v2. The
// upstream model is told to cite sources and produces tokens like
// `:cite[lumSearch3]` inline; on markdown render those tokens leak
// through, sometimes split into a clean half + an orphan tail
// (`:lumSearch3]`) when a bracket pair gets eaten by the parser.
// We don't have a UI to render them as proper footnote pills (the
// Sources section below the answer already carries the URLs), so we
// strip them — same end-state as the legacy chat had, just deliberate.
export function stripCitationTokens(text: string): string {
  return text
    // OpenAI-style bare `citeturn0search0` / `citeturn1news2` / etc.
    // emitted by chat_stream_v2 — no brackets, just inline next to the
    // sentence it cites. Captures any `cite<turnNN><any-word>NN`.
    .replace(/\s*citeturn\d+\w+?\d+/g, "")
    // Canonical `:cite[whatever]` form, with any leading whitespace.
    .replace(/\s*:cite\[[^\]]+\]/g, "")
    // Orphan tails like ` :lumSearch3]` left when the markdown parser
    // ate the leading `[:cite[`. Anchored to identifier+digits so we
    // don't eat legitimate prose ending in `]`.
    .replace(/\s*:[A-Za-z][\w-]*\d+\]/g, "")
    // Stray bullet/middle-dot separators left behind after the tokens
    // are removed (model writes "... · :cite[…] · :cite[…]").
    .replace(/[·•]\s*[·•]/g, "·")
    .replace(/[·•]\s*(?=[.,;:!?])/g, "")
    // Collapse runs of whitespace that the removals create.
    .replace(/[ \t]+(?=[.,;:!?])/g, "")
    .replace(/  +/g, " ");
}
